<template>
  <div class="flowchart">
    <div class="row">
      <div class="col-12">
        <div class="d-flex justify-content-end px-1">
          <div class="flowchart-toolbar mb-1" role="group" aria-label="Flowchart zoom">
            <button type="button" :title="$t('flowchart.zoom_in')" :aria-label="$t('flowchart.zoom_in')" @click="zoomIn">
              <feather-icon icon="ZoomInIcon" size="20" />
            </button>
            <button type="button" class="flowchart-toolbar__arrange" :disabled="isArranging" :title="$t('flowchart.arrange_nodes')" :aria-label="$t('flowchart.arrange_nodes')" :aria-busy="isArranging" @click="arrangeNodes">
              <feather-icon icon="GitBranchIcon" size="20" />
            </button>
            <button type="button" :title="$t('flowchart.fit_view')" :aria-label="$t('flowchart.fit_view')" @click="fitView">
              <feather-icon icon="MaximizeIcon" size="20" />
            </button>
            <button type="button" :title="$t('flowchart.zoom_out')" :aria-label="$t('flowchart.zoom_out')" @click="zoomOut">
              <feather-icon icon="ZoomOutIcon" size="20" />
            </button>
            <span class="flowchart-toolbar__zoom">{{ zoomPercentage }}%</span>
          </div>
        </div>
      </div>
      <div class="col-md-2">
        <!-- <b-form-input v-model="search" class="mb-3" :placeholder="$t('Search')" />
        <ul
          class="node-list list-unstyled px-1"
          style="max-height: 500px; overflow: auto"
        >
          <li
            v-for="(node, index) in filteredNodeList"
            :key="index"
            draggable="true"
            :data-node="node.item"
            class="drag-drawflow mb-1"
            @dragstart="drag($event)"
          >
            <div :class="['btn btn-block node', { custom: node.data.cmd === 'custom' }]">
              <span v-if="node.name === 'decision' || node.name === 'terminate'">
                {{ $t(`action_plan.nodes.${node.name}.name`) }}
              </span>
              <span v-else>{{ node.label }}</span>
            </div>
          </li>
        </ul> -->
        <b-form-input v-model="search" class="mb-3" :placeholder="$t('search')" />
        <div style="max-height: 500px; overflow: auto">
          <command-list
            class="mb-3"
            :list="baseCommands"
            :search="search"
            :title="$t('commands.base_commands')"
            @node-drag-start="onNodeDragStart"
          />
          <command-list
            :list="customCommands"
            :search="search"
            :title="$t('commands.custom_commands')"
            @node-drag-start="onNodeDragStart"
          />
        </div>
      </div>
      <div class="col-md-10">
        <div
          id="drawflowActionPlan"
          @drop="drop($event)"
          @dragover="allowDrop($event)"
        ></div>
      </div>
    </div>
    <command-config-modal
      :current-node-info="currentNodeInfo"
      :current-node-requirements="currentNodeRequirements"
      :flowchart-config="{ alarm_compatible: true }"
      :value="currentNodeStorage"
      @input="saveCurrentNodeStorage"
    />
  </div>
</template>

<script>
import Vue from "vue";
import OmniviewDrawflow from "@/libs/drawflow/OmniviewDrawflow";
import Terminate from "./nodes/Terminate.vue";
import Decision from "./nodes/Decision.vue";
import Node from "@/components/flowchart/nodes/commands/Node.vue"; // TODO: Rename 'Node' to 'Command'
import NodeData from "@/components/flowchart/nodes/commands/NodeData";
import CommandConfigModal from "@/views/command/components/FlowchartComponent/CommandConfigModal.vue";
import { EventBus } from "@/libs/event-bus";
import CommandList from "@/views/command/components/FlowchartComponent/CommandList.vue";
import Question from "@/views/action_plan/components/FlowchartComponent/nodes/question/Index.vue";
import DecisionSimple from "./nodes/DecisionSimple.vue";
import ShowVideo from "./nodes/ShowVideo.vue";
import PanelDeviceStatusCheck from "./nodes/PanelDeviceStatusCheck.vue";
import AlarmComment from "./nodes/AlarmComment.vue";
import RestoreAlarmType from "./nodes/RestoreAlarmType.vue";
import ValidateDeviceEvents from "./nodes/ValidateDeviceEvents.vue";
import AlarmCategory from "./nodes/AlarmCategory.vue";

export default {
  components: {
    NodeData,
    CommandConfigModal,
    CommandList,
    Question,
    DecisionSimple,
    RestoreAlarmType
  },
  props: {
    commandOptions: Array,
    flowchartData: Array,
    config: Object,
    missingNodeData: Array,
  },
  data() {
    return {
      editor: null,
      mobile_item_selec: "",
      mobile_last_move: null,
      nodeList: [],
      currentNodeId: null,
      currentNodeRequirements: null,
      currentNodeData: null,
      currentNodeStorage: null,
      showNodeData: false,
      search: "",
      currentZoom: 1,
      isArranging: false,
    };
  },
  async mounted() {
    const id = document.getElementById("drawflowActionPlan");
    Vue.prototype.$df = new OmniviewDrawflow(id, Vue, this);
    this.editor = this.$df;
    this.editor.useuuid = true;
    this.editor.on("nodeSelected", this.onNodeSelected);
    this.editor.on("nodeUnselected", this.onNodeUnselected);
    this.editor.on("nodeRemoved", this.onNodeUnselected);
    this.editor.on("zoom", this.onZoomChanged);
    // this.editor.on("connectionCreated", this.checkNodesIOs);
    // this.editor.on("connectionRemoved", this.checkNodesIOs);
    // this.editor.on("import", this.checkNodesIOs);

    this.editor.start();
    this.buildNodeList();
    this.initDraggableNodes();

    this.editor.registerNode("Terminate", Terminate, {}, {});
    this.editor.registerNode("Decision", Decision, {}, {});
    this.editor.registerNode("Node", Node, { source: "action_plan" }, {});
    this.editor.registerNode("Question", Question, {}, {});
    this.editor.registerNode("DecisionSimple", DecisionSimple, {}, {});
    this.editor.registerNode("ShowVideo", ShowVideo, {}, {});
    this.editor.registerNode("PanelDeviceStatusCheck", PanelDeviceStatusCheck, {}, {});
    this.editor.registerNode("AlarmComment", AlarmComment, {}, {});
    this.editor.registerNode("RestoreAlarmType", RestoreAlarmType, {}, {});
    this.editor.registerNode("ValidateDeviceEvents", ValidateDeviceEvents, {}, {});
    this.editor.registerNode("AlarmCategory", AlarmCategory, {}, {});
    // Here register the components of the nodes for Draflow (import first in the 'components' object)
    // this.editor.registerNode("ComponentName", ComponentName, {}, {});

    this.$emit("flowchart-ready", this.editor);

    EventBus.on("node:open-modal", this.openConfigModal);
  },
  beforeDestroy() {
    EventBus.off("node:open-modal", this.openConfigModal);
    this.editor?.destroy();
  },
  watch: {
    missingNodeData(value) {
      if (value.length) {
        value.forEach((item) => {
          const node = document.querySelector(`#node-${item.id}`);
          node.classList.add("error");
        });
      } else {
        const nodes = document.querySelectorAll(".drawflow-node.Node");
        nodes.forEach((node) => node.classList.remove("error"));
      }
    },
  },
  computed: {
    zoomPercentage() {
      return Math.round(this.currentZoom * 100);
    },
    baseCommands() {
      return this.nodeList.filter((node) => node.data && node.data.cmd !== "custom");
    },
    customCommands() {
      return this.nodeList.filter((node) => node.data && node.data.cmd === "custom");
    },
    currentNodeInfo() {
      if (!this.currentNodeData) {
        return {};
      }
      const data = this.currentNodeData.data || {};
      const cmdSlug = data.cmd ?? data.command ?? data.cmd_name;
      return {
        id: this.currentNodeData.id,
        name: data.label,
        command: cmdSlug,
        cmd: cmdSlug,
      };
    },
  },
  methods: {
    onZoomChanged(zoom) {
      this.currentZoom = zoom;
    },
    zoomIn() {
      this.editor?.zoomIn();
    },
    zoomOut() {
      this.editor?.zoomOut();
    },
    fitView() {
      this.editor?.fitView();
    },
    async arrangeNodes() {
      if (!this.editor || this.isArranging) return;

      this.isArranging = true;
      try {
        await this.editor.arrangeNodes();
      } finally {
        this.isArranging = false;
      }
    },
    saveCurrentNodeStorage(storage) {
      if (!this.currentNodeId) return;

      const data = this.editor.commitNodeData(this.currentNodeId, { storage });
      if (!data) return;

      this.currentNodeStorage = data.storage;
      this.currentNodeData = { ...this.currentNodeData, data };
      EventBus.emit("node:data-updated", this.currentNodeId);
    },
    buildNodeList() {
      const actionTree = [
        {
          _id: 1,
          name: "decision",
          label: "Decision",
          item: "Decision_1",
          input: 1,
          output: 2,
          data: { config: null, storage: null },
        },
        {
          _id: 2,
          name: "terminate",
          label: "Terminate",
          item: "Terminate_2",
          input: 1,
          output: 0,
          data: { config: null, storage: null },
        },
        {
          _id: 3,
          name: "question",
          label: "Pregunta",
          item: "Question_3",
          input: 1,
          output: 1,
          data: { config: null, storage: null },
        },
        {
          _id: 4,
          name: "decision_simple",
          label: "Decision Simple",
          item: "DecisionSimple_4",
          input: 1,
          output: 1,
          data: { config: null, storage: null },
        },
        {
          _id: 1000,
          name: "show_video",
          label: "Vista previa de vídeo e imágenes",
          item: "ShowVideo_1000",
          input: 1,
          output: 1,
          data: { config: null, storage: null, required: false },
        },
        {
          _id: 1001,
          name: "panel_device_status_check",
          label: "Comprobación de estado del dispositivo del panel",
          item: "PanelDeviceStatusCheck_1001",
          input: 1,
          output: 1,
          data: { completed: false, required: false },
        },
        {
          _id: 1002,
          name: "alarm_comments",
          label: "Comentario de alarma",
          item: "AlarmComment_1002",
          input: 1,
          output: 1,
          data: { required: false },
        },
        {
          _id: 1003,
          name: "restore_alarm_type",
          label: "Restaurar tipo de alarma",
          item: "RestoreAlarmType_1003",
          input: 1,
          output: 1,
          data: { required: true },
        },
        {
          _id: 1004,
          name: "validate_device_events",
          label: "Validar eventos de zona",
          item: "ValidateDeviceEvents_1004",
          input: 1,
          output: 1,
          data: { required: true },
        },
        {
          _id: 1005,
          name: "alarm_category",
          label: "Categoría de alarma",
          item: "AlarmCategory_1005",
          input: 1,
          output: 1,
          data: { config: {}, required: false },
        },
        // Here configure the registered components, assign a unique id, name, label and input/output
        // {
        //   _id: 1004,
        //   name: "component_name",
        //   label: "Label of the component",
        //   item: "ComponentName_1004", // <--- item must be the component name + the unique id ComponentName_[_id]
        //   input: 1,
        //   output: 1,
        //   data: { required: false },
        // },
      ];
      this.nodeList = this.nodeList.concat(actionTree).concat(
        this.commandOptions
          .filter((cmd) => cmd.cmd !== "update:alarm_category")
          .map((cmd) => ({
          _id: cmd.value,
          name: cmd.label,
          label: !cmd.custom ? this.$t(`commands.list.${cmd.label}.name`) : cmd.label,
          item: `Node_${cmd.value}`,
          input: 1,
          output: 1,
          data: { ...cmd, required: false, storage: null },
          }))
      );
    },
    onNodeSelected(id) {
      this.currentNodeId = id;
      this.currentNodeData = this.editor.getNodeFromId(id);
      if (this.currentNodeData.data.requirements) {
        this.currentNodeRequirements = this.currentNodeData.data.requirements;
        this.currentNodeStorage = this.currentNodeData.data.storage ?? null;
      }
    },
    onNodeUnselected() {
      this.currentNodeId = null;
      this.currentNodeData = null;
      this.currentNodeRequirements = null;
      this.currentNodeStorage = null;
      // this.showNodeData = false;
    },
    syncNodeRequirementsFromCatalog() {
      const currentNode = this.editor.getNodeFromId(this.currentNodeId);
      const nodeData = currentNode && currentNode.data;
      if (!nodeData) return;
      const command = (this.commandOptions || []).find(
        (option) => option.value === nodeData.value
      );
      if (!command || !Array.isArray(command.requirements)) return;
      if (
        JSON.stringify(command.requirements) ===
        JSON.stringify(nodeData.requirements)
      ) {
        return;
      }
      nodeData.requirements = command.requirements;
      this.editor.updateNodeDataFromId(this.currentNodeId, nodeData);
      this.currentNodeData = this.editor.getNodeFromId(this.currentNodeId);
    },
    async openConfigModal(nodeId) {
      this.currentNodeId = nodeId;
      this.currentNodeData = this.editor.getNodeFromId(nodeId);
      if (this.currentNodeData.data.requirements) {
        this.syncNodeRequirementsFromCatalog();
        this.currentNodeRequirements = this.currentNodeData.data.requirements;
        this.currentNodeStorage = this.currentNodeData.data.storage ?? null;
        await this.$nextTick();
        this.$root.$emit("bv::show::modal", "cmd_config_modal");
      }
    },
    async checkNodesIOs() {
      await this.$nextTick();

      const nodes = Object.values(this.editor.export().drawflow.Home.data);
      nodes.forEach((node) => {
        const currentNode = document.getElementById(`node-${node.id}`);
        const outputs = currentNode.querySelector(".outputs");
        if (node.outputs.output_1.connections.length > 0) {
          outputs.classList.add("prevent-new-outputs");
        } else {
          outputs.classList.remove("prevent-new-outputs");
        }
      });
    },
    initDraggableNodes() {
      const elements = document.getElementsByClassName("drag-drawflow");
      for (let i = 0; i < elements.length; i++) {
        elements[i].addEventListener("touchend", this.drop, false);
        elements[i].addEventListener("touchmove", this.positionMobile, false);
        elements[i].addEventListener("touchstart", this.drag, false);
      }
    },
    onNodeDragStart(data) {
      this.mobile_item_selec = data;
    },
    drop(ev) {
      if (ev.type === "touchend") {
        var parentdrawflow = document
          .elementFromPoint(
            this.mobile_last_move.touches[0].clientX,
            this.mobile_last_move.touches[0].clientY
          )
          .closest("#drawflow");
        if (parentdrawflow != null) {
          this.addNodeToDrawFlow(
            this.mobile_item_selec,
            this.mobile_last_move.touches[0].clientX,
            this.mobile_last_move.touches[0].clientY
          );
        }
        this.mobile_item_selec = "";
      } else {
        ev.preventDefault();
        var data = ev.dataTransfer.getData("node");
        this.addNodeToDrawFlow(data, ev.clientX, ev.clientY);
      }
    },
    positionMobile(ev) {
      this.mobile_last_move = ev;
    },
    allowDrop(ev) {
      ev.preventDefault();
    },
    addNodeToDrawFlow(nodeName, pos_x, pos_y) {
      pos_x =
        pos_x *
          (this.editor.precanvas.clientWidth /
            (this.editor.precanvas.clientWidth * this.editor.zoom)) -
        this.editor.precanvas.getBoundingClientRect().x *
          (this.editor.precanvas.clientWidth /
            (this.editor.precanvas.clientWidth * this.editor.zoom));
      pos_y =
        pos_y *
          (this.editor.precanvas.clientHeight /
            (this.editor.precanvas.clientHeight * this.editor.zoom)) -
        this.editor.precanvas.getBoundingClientRect().y *
          (this.editor.precanvas.clientHeight /
            (this.editor.precanvas.clientHeight * this.editor.zoom));

      const [name, id] = nodeName.split("_");
      const nodeSelected = this.nodeList.find((ele) => ele._id == id);

      this.editor.addNode(
        nodeSelected.data ? nodeSelected.data.label : nodeSelected.name,
        nodeSelected.input,
        nodeSelected.output,
        pos_x,
        pos_y,
        name,
        nodeSelected.data || {},
        name,
        "vue"
      );
    },
  },
};
</script>

<style lang="scss">
.node-data-config {
  margin-bottom: 10px;
  min-height: 65px;
  background-color: #fff;
  transition: all 0.4s;
  opacity: 0;
  &.show {
    opacity: 1;
  }
}
</style>
